/**
 * SSO providers for one tenant.
 *
 * Writes Open edX's own `OAuth2ProviderConfig` rows, which are keyed on
 * (site_id, backend_name) and therefore already per-tenant upstream. Nothing is stored
 * in a table of ours, and the model is a ConfigurationModel — saving appends a new
 * version, so every change keeps its history.
 *
 * Secrets are write-only. The API returns `has_secret` rather than the value, and
 * leaving the field blank on save keeps the existing credential — so toggling
 * "enabled" cannot silently wipe it.
 */

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Banner, Button, Card, Field, Pill, SelectField } from '../../shared/ui';
import { openedx } from '../../shared/lib/openedx';

interface Configured {
  backend_name: string;
  label: string;
  name: string;
  enabled: boolean;
  visible: boolean;
  has_secret: boolean;
  key: string;
  changed_at: string | null;
}

interface ProvidersPayload {
  configured: Configured[];
  backends: { backend_name: string; label: string }[];
}

export function ProvidersCard({ slug }: { slug: string }) {
  const queryClient = useQueryClient();
  const [adding, setAdding] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['providers', slug],
    queryFn: () => openedx.get<ProvidersPayload>(`/api/tenant/v1/tenants/${slug}/providers`),
  });

  const save = useMutation({
    mutationFn: (payload: Record<string, unknown>) =>
      openedx.post(`/api/tenant/v1/tenants/${slug}/providers`, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['providers', slug] });
      setAdding(false);
    },
  });

  if (isLoading || !data) return <Card title="Sign-in providers">Loading…</Card>;

  return (
    <Card
      title="Sign-in providers"
      action={
        !adding ? (
          <Button onClick={() => setAdding(true)}>Add provider</Button>
        ) : undefined
      }
    >
      {data.configured.length === 0 && !adding && (
        <p className="console__hint">
          No SSO providers yet. Learners on this academy sign in with a password only.
        </p>
      )}

      {data.configured.length > 0 && (
        <div className="ui-table-scroll">
          <table className="ui-table">
            <thead>
              <tr>
                <th>Provider</th>
                <th>Client ID</th>
                <th>Secret</th>
                <th>Status</th>
                <th aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {data.configured.map((p) => (
                <tr key={p.backend_name}>
                  <td>
                    <strong>{p.name}</strong>
                    <div className="console__inherited">{p.backend_name}</div>
                  </td>
                  <td className="mono">{p.key || '—'}</td>
                  <td>
                    {p.has_secret ? (
                      <Pill tone="active">set</Pill>
                    ) : (
                      <Pill tone="suspended">missing</Pill>
                    )}
                  </td>
                  <td>
                    <Pill tone={p.enabled ? 'active' : 'retired'}>
                      {p.enabled ? 'enabled' : 'disabled'}
                    </Pill>
                  </td>
                  <td>
                    <Button
                      busy={save.isPending}
                      onClick={() =>
                        save.mutate({ backend_name: p.backend_name, enabled: !p.enabled })
                      }
                    >
                      {p.enabled ? 'Disable' : 'Enable'}
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {adding && (
        <AddProvider
          backends={data.backends}
          busy={save.isPending}
          error={save.isError}
          onCancel={() => setAdding(false)}
          onSave={(payload) => save.mutate(payload)}
        />
      )}

      <p className="console__hint">
        Learners see these on this academy&rsquo;s sign-in page. Redirect URI to register
        with the provider:{' '}
        <code>{`{LMS}/auth/complete/<backend>/`}</code>
      </p>
    </Card>
  );
}

function AddProvider({
  backends,
  busy,
  error,
  onCancel,
  onSave,
}: {
  backends: { backend_name: string; label: string }[];
  busy: boolean;
  error: boolean;
  onCancel: () => void;
  onSave: (payload: Record<string, unknown>) => void;
}) {
  const [backend, setBackend] = useState(backends[0]?.backend_name ?? '');
  const [name, setName] = useState('');
  const [key, setKey] = useState('');
  const [secret, setSecret] = useState('');

  return (
    <form
      className="console__form"
      style={{ marginTop: 'var(--space-4)' }}
      onSubmit={(e) => {
        e.preventDefault();
        onSave({ backend_name: backend, name, key, secret, enabled: true });
      }}
    >
      <SelectField
        label="Provider"
        name="backend"
        value={backend}
        onChange={(e) => setBackend(e.target.value)}
        options={backends.map((b) => [b.backend_name, b.label])}
      />
      <Field
        label="Button label"
        name="name"
        value={name}
        onChange={(e) => setName(e.target.value)}
        hint="What learners see on the sign-in button. Defaults to the provider name."
      />
      <Field
        label="Client ID"
        name="key"
        value={key}
        onChange={(e) => setKey(e.target.value)}
        required
      />
      <Field
        label="Client secret"
        name="secret"
        type="password"
        autoComplete="off"
        value={secret}
        onChange={(e) => setSecret(e.target.value)}
        hint="Stored write-only. Leave blank when editing to keep the current secret."
        required
      />

      {error && (
        <Banner tone="danger">
          <strong>That provider was not saved.</strong> Check the client ID and secret.
        </Banner>
      )}

      <div className="console__form-actions">
        <Button type="submit" variant="primary" busy={busy}>
          Save provider
        </Button>
        <Button variant="ghost" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </form>
  );
}
